<template>
  <div class="accommodation-container">
    <v-card class="date-selection-card mb-4 elevation-2">
      <v-card-title class="date-selection-header">
        <h3 class="earthwerk-font">Select Your Dates</h3>
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="hasBookingDates"
          type="info"
          class="mb-3"
          icon="mdi-calendar-check"
          dense
          dismissible>
          <strong>Your selected stay dates:</strong> {{ formattedCheckIn }} to {{ formattedCheckOut }}
          <span class="ml-2 grey--text text-caption">({{ bookingDays }} days)</span>
          <br>
          <small class="text-caption">You can modify dates below if needed (must be within your stay period).</small>
        </v-alert>

        <v-alert
          v-if="hasGlobalGuests"
          type="success"
          class="mb-3"
          icon="mdi-account-group"
          dense
          dismissible>
          <strong>Your selected guests:</strong> {{ formattedGlobalGuests }}
          <br>
          <small class="text-caption">Guest numbers will be pre-filled when adding accommodations.</small>
        </v-alert>

        <v-layout row wrap>
          <v-flex md6 sm12 class="pa-2">
            <v-dialog
              ref="startDateDialog"
              persistent
              v-model="modalStartDate"
              lazy
              full-width
              width="290px"
              :return-value.sync="facility.start_date">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  :label="hasBookingDates ? 'Start Date (Optional)' : 'Start Date *'"
                  v-model="facility.start_date"
                  readonly
                  prepend-icon="event"
                  :hint="hasBookingDates ? `Must be between ${formattedCheckIn} and ${formattedCheckOut}` : ''"
                  persistent-hint
                  class="date-input"></v-text-field>
              </template>
              <v-date-picker
                v-model="facility.start_date"
                scrollable
                :min="minStartDate"
                :max="maxStartDate"
                @input="onStartDatePicked">
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="modalStartDate = false">Cancel</v-btn>
                <v-btn flat color="primary" @click="$refs.startDateDialog.save(facility.start_date)">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>

          <v-flex md6 sm12 class="pa-2">
            <v-dialog
              ref="endDateDialog"
              persistent
              v-model="modalEndDate"
              lazy
              full-width
              width="290px"
              :return-value.sync="facility.end_date">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  :label="hasBookingDates ? 'End Date (Optional)' : 'End Date *'"
                  v-model="facility.end_date"
                  readonly
                  :disabled="!facility.start_date"
                  prepend-icon="event"
                  :hint="hasBookingDates ? `Must be after start date and within ${formattedCheckOut}` : ''"
                  persistent-hint
                  class="date-input"></v-text-field>
              </template>
              <v-date-picker
                v-model="facility.end_date"
                scrollable
                :min="minEndDate"
                :max="maxEndDate"
                @input="onEndDatePicked">
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="modalEndDate = false">Cancel</v-btn>
                <v-btn flat color="primary" @click="$refs.endDateDialog.save(facility.end_date)">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>
        </v-layout>

        <v-flex v-if="!isSearching && facility.start_date && facility.end_date && allAvailabileFacilities && allAvailabileFacilities.length > 0">
          <h4 class="earth-color" aria-live="polite">AVAILABLE ACCOMMODATIONS: {{ allAvailabileFacilities.length }}</h4>
          <small class="slots-explanation">
            Showing facilities available from {{ facility.start_date }} to {{ facility.end_date }}
          </small>
        </v-flex>
      </v-card-text>
    </v-card>

    <div class="accommodations-grid-container">
      <div v-if="isSearching" class="grid-wrapper" aria-busy="true" aria-live="polite">
        <div v-for="n in 6" :key="'sk-' + n" class="grid-item">
          <div class="accommodation-card skeleton-card">
            <div class="skeleton-image shimmer"></div>
            <div class="card-body">
              <div class="skeleton-line shimmer w-70"></div>
              <div class="skeleton-line shimmer w-40"></div>
              <div class="skeleton-line shimmer w-55 mt-16"></div>
              <div class="skeleton-btn shimmer"></div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="allAvailabileFacilities && allAvailabileFacilities.length > 0"
        class="grid-wrapper">
        <div
          v-for="accommodation in allAvailabileFacilities"
          :key="accommodation.public_id"
          class="grid-item">

          <div class="accommodation-card">
            <div class="card-image-wrap" @click="openInfo(accommodation)">
              <v-img
                class="card-image"
                :src="accommodation.thumbnail"
                aspect-ratio="1.6"
                gradient="to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.15)">
              </v-img>
              <div class="price-badge" v-if="accommodation.pricing_status === 'true'">
                <template v-if="accommodation.accomodation_type_id === 'Stables'">
                  Base {{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].mandatory_price).amount) }}
                </template>
                <template v-else-if="isCamping(accommodation)">
                  From {{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].child_price).amount) }}
                </template>
                <template v-else>
                  From {{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].price).amount) }}
                </template>
              </div>
            </div>

            <div class="card-body">
              <div class="title-row">
                <h3 class="card-title earthwerk-font" @click="openInfo(accommodation)">
                  {{ accommodation.name.toUpperCase() }}
                </h3>
                <button class="info-btn" @click="openInfo(accommodation)">
                  <v-icon small color="#74a533">mdi-information-outline</v-icon>
                </button>
              </div>

              <div class="meta-row">
                <v-icon x-small color="#7b8f6b">mdi-account-group</v-icon>
                <span>Max {{ accommodation.maximum_guests || 10 }} guests</span>
                <template v-if="accommodation.quantity">
                  <span class="mx-1">·</span>
                  <v-icon x-small color="#7b8f6b">mdi-home-group</v-icon>
                  <span>{{ accommodation.quantity }} units</span>
                </template>
              </div>

              <div class="price-row" v-if="accommodation.pricing_status === 'true'">
                <div v-if="accommodation.accomodation_type_id === 'Stables'" class="price-line">
                  <span>Price</span>
                  <span class="price-value">{{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].mandatory_price).amount) }}</span>
                </div>
                <div v-else-if="isCamping(accommodation)">
                  <div class="price-line">
                    <span>Adult / night</span>
                    <span class="price-value">{{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].adult_price).amount) }}</span>
                  </div>
                  <div class="price-line">
                    <span>Child / night</span>
                    <span class="price-value">{{ $localCurrency }} {{ $roundOff(convert1(accommodation.price[0].currency_type, accommodation.price[0].child_price).amount) }}</span>
                  </div>
                </div>
                <div v-else>
                  <div v-for="(price, key) in developPricePacks(accommodation)" :key="key" class="price-line">
                    <span>{{ price.lower_limit }}–{{ price.upper_limit }} guests</span>
                    <span class="price-value">{{ $localCurrency }} {{ $roundOff(convert1(price.currency, price.price).amount) }}</span>
                  </div>
                </div>
              </div>

              <button
                class="add-btn"
                :disabled="accommodation.pricing_status !== 'true'"
                @click="openBookingDialog(accommodation)">
                <v-icon small color="white" class="mr-1">mdi-plus</v-icon>
                Add to Booking
              </button>
            </div>
          </div>

        </div>
      </div>

      <v-layout row wrap v-else-if="(!facility.start_date || !facility.end_date)">
        <v-flex xs12>
          <v-card class="text-center pa-5 empty-state">
            <v-icon size="64" color="grey lighten-2">event</v-icon>
            <h4 class="grey--text">Please select start and end dates to see available accommodations</h4>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-else>
        <v-flex xs12>
          <v-card class="text-center pa-5 empty-state">
            <v-icon size="64" color="grey lighten-2">hotel</v-icon>
            <h4 class="grey--text">No accommodations available for the selected dates</h4>
            <p class="grey--text">Please try different dates</p>
            <v-btn color="primary" class="earthwerk-font mt-3" @click="resetForAvailability">
              Change Dates
            </v-btn>
          </v-card>
        </v-flex>
      </v-layout>
    </div>

    <v-dialog v-model="infoDialog" persistent max-width="900px">
      <v-card class="info-dialog">
        <div class="gallery-section">
          <div v-if="infoImages && infoImages.length > 0" class="gallery-container">
            <div class="image-navigation">
              <button class="nav-button prev" @click="prevInfoImage" :disabled="currentInfoImageIndex === 0">
                <v-icon>chevron_left</v-icon>
              </button>
              <div class="image-wrapper">
                <img :src="infoImages[currentInfoImageIndex].path" class="gallery-image" alt="Facility image">
              </div>
              <button class="nav-button next" @click="nextInfoImage" :disabled="currentInfoImageIndex === infoImages.length - 1">
                <v-icon>chevron_right</v-icon>
              </button>
            </div>
            <div class="image-counter">
              {{ currentInfoImageIndex + 1 }} / {{ infoImages.length }}
            </div>
          </div>
          <div v-else class="single-image-container">
            <img :src="currentInfoFacility ? currentInfoFacility.thumbnail : ''" class="gallery-image" alt="Facility thumbnail">
          </div>
        </div>

        <v-card-text class="details-section">
          <div class="details-header">
            <h1 class="facility-title earthwerk-font">{{ currentInfoFacility ? currentInfoFacility.name.toUpperCase() : '' }}</h1>
            <div class="capacity-display">
              <div class="capacity-item">
                <span class="capacity-label">Maximum Capacity:</span>
                <span class="capacity-value">{{ currentInfoFacility ? currentInfoFacility.maximum_guests : '' }} Guests</span>
              </div>
            </div>
          </div>
          <div class="description-section" v-html="currentInfoFacility ? currentInfoFacility.description : ''"></div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="infoDialog = false" class="earthwerk-font">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="800px" @keydown.esc="closeBookingDialog">
      <v-card class="booking-dialog">
        <v-card-title class="dialog-header">
          <h2 class="earthwerk-font">ADD TO BOOKING</h2>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeBookingDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-tabs color="primary" dark slider-color="primary">
            <v-tab key="availability" class="earthwerk-font">Accommodation Details</v-tab>
            <v-tab-item key="availability">
              <div class="tab-content">
                <div class="date-pickers">
                  <v-text-field label="Start Date" v-model="bookingDetails.start_date" readonly prepend-icon="event" class="date-input" disabled></v-text-field>
                  <v-text-field label="End Date" v-model="bookingDetails.end_date" readonly prepend-icon="event" class="date-input" disabled></v-text-field>
                </div>

                <v-flex v-if="bookingDetails.start_date && bookingDetails.end_date && selectedFacility">
                  <h4 class="earth-color">AVAILABLE SLOTS : {{ availableSlots }}</h4>
                  <small class="slots-explanation">
                    (Maximum Capacity: {{ selectedFacility.maximum_guests }} × Available Units: {{ selectedFacility.quantity || 1 }})
                  </small>
                </v-flex>

                <div class="participant-selectors" v-if="showDates && bookingDetails.start_date && bookingDetails.end_date">
                  <div class="selector-group">
                    <v-autocomplete label="Children" :items="childSlots" v-model="bookingDetails.children" prepend-icon="child_friendly" class="participant-input" @change="calculateExtraPax"></v-autocomplete>
                    <v-autocomplete label="Adults" :items="adultSlots" v-model="bookingDetails.adults" prepend-icon="person" class="participant-input" @change="calculateExtraPax"></v-autocomplete>
                  </div>
                </div>

                <div v-if="hasGlobalGuests && (bookingDetails.adults > 0 || bookingDetails.children > 0)" class="global-guests-info">
                  <v-alert type="info" dense class="mb-2">
                    <v-icon small left>mdi-account-multiple</v-icon>
                    Booking for: {{ bookingDetails.adults }} Adult{{ bookingDetails.adults !== 1 ? 's' : '' }} and {{ bookingDetails.children }} Child{{ bookingDetails.children !== 1 ? 'ren' : '' }}
                  </v-alert>
                </div>

                <div v-if="showExtraPaxSection" class="extra-pax-section">
                  <v-divider class="my-3"></v-divider>
                  <h4 class="earth-color">Extra Participants (Exceeds Standard Capacity)</h4>
                  <small class="slots-explanation">Additional charges apply for extra guests</small>
                  <div class="additional-options mt-3">
                    <v-text-field label="Extra Adults" type="number" min="0" v-model="bookingDetails.extra_pax_adults" prepend-icon="person_add" class="option-input" @change="validateExtraPax"></v-text-field>
                    <v-text-field label="Extra Children" type="number" min="0" v-model="bookingDetails.extra_pax_children" prepend-icon="child_care" class="option-input" @change="validateExtraPax"></v-text-field>
                  </div>
                </div>

                <div class="additional-options" v-if="selectedFacility && selectedFacility.accomodation_type_id === 'Stables' && (selectedFacility.name === 'The Stables Research Centre - Double Room (One Bed)' || selectedFacility.name === 'The Stables Research Centre - Twin Room (Two Beds)')">
                  <v-divider class="my-3"></v-divider>
                  <v-select label="Number of Rooms" :items="roomSlots" v-model="bookingDetails.number_of_rooms" prepend-icon="hotel" class="option-input"></v-select>
                </div>

                <div class="additional-options" v-if="selectedFacility && selectedFacility.accomodation_type_id === 'Pelican'">
                  <v-divider class="my-3"></v-divider>
                  <v-select label="Catering Type" :items="cateringType" item-text="facility_pricing_name" item-value="facility_pricing_type_public_id" v-model="bookingDetails.catering_type" prepend-icon="restaurant" class="option-input"></v-select>
                </div>

                <div class="price-summary" v-if="(bookingDetails.adults > 0 || bookingDetails.children > 0) && bookingDetails.start_date && bookingDetails.end_date">
                  <h3 class="earthwerk-font">Price Summary</h3>
                  <div class="price-breakdown">
                    <div v-if="selectedFacility && selectedFacility.accomodation_type_id === 'Stables'">
                      <div v-if="selectedFacility.maximum_guests == 1 || selectedFacility.maximum_guests == 2" class="price-line">
                        <span>Room Rate ({{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateTotalPrice()) }}</span>
                      </div>
                      <div v-else>
                        <div class="price-line" v-if="bookingDetails.adults > 0">
                          <span>Adults ({{ bookingDetails.adults }} × {{ $localCurrency }} {{ $roundOff(getAdultPrice()) }} × {{ getDays() }} nights)</span>
                          <span>{{ $localCurrency }} {{ $roundOff(calculateAdultTotal()) }}</span>
                        </div>
                        <div class="price-line" v-if="bookingDetails.children > 0">
                          <span>Children ({{ bookingDetails.children }} × {{ $localCurrency }} {{ $roundOff(getChildPrice()) }} × {{ getDays() }} nights)</span>
                          <span>{{ $localCurrency }} {{ $roundOff(calculateChildTotal()) }}</span>
                        </div>
                      </div>
                    </div>

                    <div v-else-if="selectedFacility && selectedFacility.accomodation_type_id === 'Pelican' && bookingDetails.catering_type">
                      <div class="price-line">
                        <span>Package Rate ({{ getDays() }} nights)</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateTotalPrice()) }}</span>
                      </div>
                    </div>

                    <div v-else-if="selectedFacility">
                      <div class="price-line" v-if="bookingDetails.adults > 0">
                        <span>Adults ({{ bookingDetails.adults }} × {{ $localCurrency }} {{ $roundOff(getAdultPrice()) }} × {{ getDays() }} nights)</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateAdultTotal()) }}</span>
                      </div>
                      <div class="price-line" v-if="bookingDetails.children > 0">
                        <span>Children ({{ bookingDetails.children }} × {{ $localCurrency }} {{ $roundOff(getChildPrice()) }} × {{ getDays() }} nights)</span>
                        <span>{{ $localCurrency }} {{ $roundOff(calculateChildTotal()) }}</span>
                      </div>
                    </div>

                    <div v-if="bookingDetails.extra_pax_adults > 0" class="price-line extra-charge">
                      <span>Extra Adults ({{ bookingDetails.extra_pax_adults }} × {{ $localCurrency }} {{ $roundOff(getExtraAdultRate()) }} × {{ getDays() }} nights)</span>
                      <span>{{ $localCurrency }} {{ $roundOff(calculateExtraAdultTotal()) }}</span>
                    </div>
                    <div v-if="bookingDetails.extra_pax_children > 0" class="price-line extra-charge">
                      <span>Extra Children ({{ bookingDetails.extra_pax_children }} × {{ $localCurrency }} {{ $roundOff(getExtraChildRate()) }} × {{ getDays() }} nights)</span>
                      <span>{{ $localCurrency }} {{ $roundOff(calculateExtraChildTotal()) }}</span>
                    </div>

                    <div class="price-total" v-if="calculateTotalPriceWithExtras() > 0">
                      <strong>Total Amount:</strong>
                      <strong>{{ $localCurrency }} {{ $roundOff(calculateTotalPriceWithExtras()) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="closeBookingDialog" class="cancel-btn earthwerk-font">Cancel</v-btn>
          <v-btn color="primary" @click="addAccommodation()" class="reserve-btn earthwerk-font">Add to Booking</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'Accommodations',
  data() {
    return {
      dialog: false,
      infoDialog: false,
      currentInfoFacility: null,
      currentInfoImageIndex: 0,
      selectedFacility: null,
      modalStartDate: false,
      modalEndDate: false,
      today: this.$moment().format('YYYY-MM-DD'),
      showDates: false,
      showExtraPaxSection: false,
      isSearching: false,
      _skeletonTimer: null,
      facility: {
        start_date: '',
        end_date: ''
      },
      bookingDetails: {
        start_date: '',
        end_date: '',
        children: 0,
        adults: 0,
        catering_type: '',
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0,
        number_of_rooms: 0
      },
      pricingWatcher: null,
      lastSearchedRange: null,
      storedBooking: {
        check_in: '',
        check_out: '',
        days: 0
      }
    }
  },

  created() {
    if (this.$store.dispatch) {
      if (this.$store._actions && this.$store._actions['get_active_camping']) {
        this.$store.dispatch('get_active_camping').catch(function () {})
      }
      if (this.$store._actions && this.$store._actions['get_active_accommodation']) {
        this.$store.dispatch('get_active_accommodation').catch(function () {})
      }
      if (this.$store._actions && this.$store._actions['get_all_facility_pricing_types']) {
        this.$store.dispatch('get_all_facility_pricing_types').catch(function () {})
      }
    }
    this.loadDatesFromStorage()
    this.loadGuestsFromStorage()
    this.$root.$on('booking-dates-changed', this.onBookingDatesChanged)
  },

  mounted() {
    this.autoSearchIfDatesAvailable()
  },

  beforeDestroy() {
    if (this.pricingWatcher) {
      this.pricingWatcher()
    }
    if (this._skeletonTimer) {
      clearTimeout(this._skeletonTimer)
      this._skeletonTimer = null
    }
    this.$root.$off('booking-dates-changed', this.onBookingDatesChanged)
  },

  computed: {
    hasBookingDates() {
      return !!(this.storedBooking.check_in && this.storedBooking.check_out)
    },
    formattedCheckIn() {
      if (!this.storedBooking.check_in) return ''
      return this.$moment(this.storedBooking.check_in).format('MMM DD, YYYY')
    },
    formattedCheckOut() {
      if (!this.storedBooking.check_out) return ''
      return this.$moment(this.storedBooking.check_out).format('MMM DD, YYYY')
    },
    bookingDays() {
      return this.storedBooking.days || 0
    },

    globalGuests() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_guests) {
        return this.$store.state.GlobalBookingStore.booking_guests
      }
      return { adults: 0, children: 0, total: 0 }
    },
    hasGlobalGuests() {
      return this.globalGuests.total > 0
    },
    formattedGlobalGuests() {
      if (!this.hasGlobalGuests) return ''
      return this.globalGuests.adults + ' Adult' + (this.globalGuests.adults !== 1 ? 's' : '') + ' and ' + this.globalGuests.children + ' Child' + (this.globalGuests.children !== 1 ? 'ren' : '')
    },

    standardCapacity() {
      if (!this.selectedFacility) return 0
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var units = Number(this.selectedFacility.quantity || 1)
      return maxGuests * units
    },
    totalStandardGuests() {
      return Number(this.bookingDetails.adults || 0) + Number(this.bookingDetails.children || 0)
    },

    minStartDate() {
      if (this.hasBookingDates) return this.storedBooking.check_in
      return this.today
    },
    maxStartDate() {
      if (this.hasBookingDates && this.storedBooking.check_out) {
        return this.$moment(this.storedBooking.check_out).subtract(1, 'days').format('YYYY-MM-DD')
      }
      return null
    },
    minEndDate() {
      if (this.facility.start_date) {
        return this.$moment(this.facility.start_date).add(1, 'days').format('YYYY-MM-DD')
      }
      if (this.hasBookingDates) {
        return this.$moment(this.storedBooking.check_in).add(1, 'days').format('YYYY-MM-DD')
      }
      return this.today
    },
    maxEndDate() {
      if (this.hasBookingDates) return this.storedBooking.check_out
      return null
    },

    allAvailabileFacilities() {
      var facilities = []
      if (this.$store.state.BookingStore && this.$store.state.BookingStore.available_facilities) {
        facilities = this.$store.state.BookingStore.available_facilities
      } else if (this.$store.state.FacilityStore && this.$store.state.FacilityStore.available_facilities) {
        facilities = this.$store.state.FacilityStore.available_facilities
      } else if (this.$store.state.available_facilities) {
        facilities = this.$store.state.available_facilities
      }
      if (!Array.isArray(facilities)) return []
      return facilities.filter(function (f) {
        return f.pricing_status === 'true'
      })
    },

    pricedFacility() {
      if (this.$store.state.FacilityStore) {
        return this.$store.state.FacilityStore.facility_with_pricing || this.selectedFacility
      }
      return this.selectedFacility
    },
    infoImages() {
      if (this.$store.state.FacilityStore) {
        return this.$store.state.FacilityStore.facility_images || []
      }
      return []
    },
    cateringType() {
      if (this.$allPricingGroups && Array.isArray(this.$allPricingGroups)) {
        return this.$allPricingGroups
      }
      return []
    },
    availableSlots() {
      if (!this.selectedFacility) return 0
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var quantity = Number(this.selectedFacility.quantity || 1)
      return maxGuests * quantity
    },
    childSlots() {
      var max = this.calculateChildMax
      var slots = []
      for (var i = 0; i <= max; i++) slots.push(i)
      return slots
    },
    adultSlots() {
      var max = this.calculateAdultMax
      var slots = []
      for (var i = 0; i <= max; i++) slots.push(i)
      return slots
    },
    roomSlots() {
      var available = this.selectedFacility ? Number(this.selectedFacility.quantity || 1) : 1
      var slots = []
      for (var i = 0; i <= available; i++) slots.push(i)
      return slots
    },
    calculateAdultMax() {
      if (!this.selectedFacility) return 10
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var quantity = Number(this.selectedFacility.quantity || 1)
      var totalCapacity = maxGuests * quantity
      var totalGuests = Number(this.total || 0)
      var currentAdults = Number(this.bookingDetails.adults || 0)
      return Math.max(0, (totalCapacity - totalGuests) + currentAdults)
    },
    calculateChildMax() {
      if (!this.selectedFacility) return 10
      var maxGuests = Number(this.selectedFacility.maximum_guests || 0)
      var quantity = Number(this.selectedFacility.quantity || 1)
      var totalCapacity = maxGuests * quantity
      var totalGuests = Number(this.total || 0)
      var currentChildren = Number(this.bookingDetails.children || 0)
      return Math.max(0, (totalCapacity - totalGuests) + currentChildren)
    },
    total() {
      return Number(this.bookingDetails.adults || 0) + Number(this.bookingDetails.children || 0)
    }
  },

  methods: {
    isCamping(facility) {
      return (
        facility.facility_type_id === 'Camping Sites' ||
        facility.facility_type === 'Camping Sites' ||
        (facility.name && facility.name.toLowerCase().indexOf('campsite') !== -1)
      )
    },

    loadDatesFromStorage() {
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}')
        this.storedBooking = {
          check_in: booking.check_in || '',
          check_out: booking.check_out || '',
          days: booking.days || 0
        }
        if (booking.check_in && booking.check_out) {
          this.facility.start_date = booking.check_in
          this.facility.end_date = booking.check_out
        }
      } catch (e) {
        this.storedBooking = { check_in: '', check_out: '', days: 0 }
      }
    },

    saveDatesToStorage() {
      if (!this.facility.start_date || !this.facility.end_date) return
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}')
        booking.check_in = this.facility.start_date
        booking.check_out = this.facility.end_date
        booking.days = Math.max(
          1,
          this.$moment(this.facility.end_date).diff(this.$moment(this.facility.start_date), 'days')
        )
        localStorage.setItem('booking', JSON.stringify(booking))
        this.storedBooking = {
          check_in: booking.check_in,
          check_out: booking.check_out,
          days: booking.days
        }
      } catch (e) {}
    },

    getStorageAdults() {
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{}')
        if (guests.adults !== undefined && guests.adults !== null && guests.adults !== '') {
          return Number(guests.adults || 0)
        }
      } catch (e) {}
      try {
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        if (Array.isArray(conservation) && conservation.length > 0) {
          return conservation.reduce(function (sum, fee) {
            const type = fee.payment_person ? fee.payment_person.toLowerCase() : ''
            if (type.includes('child') || type.includes('infant')) return sum
            return sum + Number(fee.payment_guests || 0)
          }, 0)
        }
      } catch (e) {}
      return 0
    },

    getStorageChildren() {
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{}')
        if (guests.children !== undefined && guests.children !== null && guests.children !== '') {
          return Number(guests.children || 0)
        }
      } catch (e) {}
      try {
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        if (Array.isArray(conservation) && conservation.length > 0) {
          return conservation.reduce(function (sum, fee) {
            const type = fee.payment_person ? fee.payment_person.toLowerCase() : ''
            if (!type.includes('child')) return sum
            if (type.includes('infant')) return sum
            return sum + Number(fee.payment_guests || 0)
          }, 0)
        }
      } catch (e) {}
      return 0
    },

    loadGuestsFromStorage() {
      var adults = this.getStorageAdults()
      var children = this.getStorageChildren()
      if ((adults === 0 && children === 0) && this.hasGlobalGuests) {
        adults = this.globalGuests.adults
        children = this.globalGuests.children
      }
      if (adults > 0 || children > 0) {
        this.bookingDetails.adults = adults
        this.bookingDetails.children = children
      }
    },

    autoSearchIfDatesAvailable() {
      if (!this.facility.start_date || !this.facility.end_date) return
      const range = `${this.facility.start_date}|${this.facility.end_date}`
      if (this.lastSearchedRange === range) return
      this.searchFacilityAvailability()
    },

    onBookingDatesChanged(dates) {
      if (!dates || !dates.check_in || !dates.check_out) return
      const same = this.facility.start_date === dates.check_in &&
                   this.facility.end_date === dates.check_out
      if (same) return
      this.facility.start_date = dates.check_in
      this.facility.end_date = dates.check_out
      this.saveDatesToStorage()
      this.searchFacilityAvailability()
    },

    onStartDatePicked() {
      if (this.facility.end_date && this.facility.end_date <= this.facility.start_date) {
        this.facility.end_date = ''
      }
      this.saveDatesToStorage()
      if (this.facility.start_date && this.facility.end_date) {
        this.searchFacilityAvailability()
      }
    },

    onEndDatePicked() {
      this.saveDatesToStorage()
      if (this.facility.start_date && this.facility.end_date) {
        this.searchFacilityAvailability()
      }
    },

    calculateExtraPax() {
  const standardCapacity = this.standardCapacity
  const totalGuests = this.totalStandardGuests
  if (totalGuests > standardCapacity && standardCapacity > 0) {
    this.showExtraPaxSection = true
    const extraCount = totalGuests - standardCapacity
    let extraAdults = 0
    let extraChildren = 0
    if (this.bookingDetails.adults > 0 && this.bookingDetails.children > 0) {
      const adultRatio = this.bookingDetails.adults / totalGuests
      extraAdults = Math.round(extraCount * adultRatio)
      extraChildren = extraCount - extraAdults
      if (extraChildren > this.bookingDetails.children) {
        extraChildren = this.bookingDetails.children
        extraAdults = extraCount - extraChildren
      }
      if (extraAdults > this.bookingDetails.adults) {
        extraAdults = this.bookingDetails.adults
        extraChildren = extraCount - extraAdults
      }
    } else if (this.bookingDetails.adults > 0) {
      extraAdults = Math.min(extraCount, this.bookingDetails.adults)
      extraChildren = 0
    } else if (this.bookingDetails.children > 0) {
      extraAdults = 0
      extraChildren = Math.min(extraCount, this.bookingDetails.children)
    }
    this.bookingDetails.extra_pax_adults = extraAdults
    this.bookingDetails.extra_pax_children = extraChildren
  } else {
    this.showExtraPaxSection = false
    this.bookingDetails.extra_pax_adults = 0
    this.bookingDetails.extra_pax_children = 0
  }
},

    validateExtraPax() {
      if (this.bookingDetails.extra_pax_adults < 0) this.bookingDetails.extra_pax_adults = 0
      if (this.bookingDetails.extra_pax_children < 0) this.bookingDetails.extra_pax_children = 0
      if (isNaN(this.bookingDetails.extra_pax_adults)) this.bookingDetails.extra_pax_adults = 0
      if (isNaN(this.bookingDetails.extra_pax_children)) this.bookingDetails.extra_pax_children = 0
    },

    searchFacilityAvailability() {
      var self = this
      if (!this.facility.start_date || !this.facility.end_date) return

      const range = `${this.facility.start_date}|${this.facility.end_date}`
      this.lastSearchedRange = range

      var actionToUse = null
      if (this.$store._actions && this.$store._actions['check_date_facility_public_availability']) {
        actionToUse = 'check_date_facility_public_availability'
      } else if (this.$store._actions && this.$store._actions['check_date_facility_availability']) {
        actionToUse = 'check_date_facility_availability'
      }
      if (!actionToUse) return

      if (this._skeletonTimer) {
        clearTimeout(this._skeletonTimer)
        this._skeletonTimer = null
      }
      this._skeletonTimer = setTimeout(() => {
        if (this.lastSearchedRange === range) this.isSearching = true
      }, 250)

      if (this.$store.commit) {
        this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
      }

      this.$store.dispatch(actionToUse, {
        check_in_date: this.facility.start_date,
        check_out_date: this.facility.end_date
      })
        .then(function () {
          if (self._skeletonTimer) {
            clearTimeout(self._skeletonTimer)
            self._skeletonTimer = null
          }
          self.isSearching = false
        })
        .catch(function () {
          if (self._skeletonTimer) {
            clearTimeout(self._skeletonTimer)
            self._skeletonTimer = null
          }
          self.isSearching = false
        })
    },

    resetForAvailability() {
      if (this._skeletonTimer) {
        clearTimeout(this._skeletonTimer)
        this._skeletonTimer = null
      }
      this.facility.end_date = ''
      if (this.$store.commit) {
        this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
      }
      this.selectedFacility = null
      this.isSearching = false
    },

    openInfo(accommodation) {
      this.currentInfoFacility = accommodation
      this.currentInfoImageIndex = 0
      if (this.$store.dispatch && this.$store._actions && this.$store._actions['get_facility_images']) {
        this.$store.dispatch('get_facility_images', accommodation.public_id)
      }
      this.infoDialog = true
    },
    prevInfoImage() {
      if (this.currentInfoImageIndex > 0) this.currentInfoImageIndex--
    },
    nextInfoImage() {
      if (this.currentInfoImageIndex < this.infoImages.length - 1) this.currentInfoImageIndex++
    },

    openBookingDialog(accommodation) {
      this.selectedFacility = accommodation
      var storedAdults = this.getStorageAdults()
      var storedChildren = this.getStorageChildren()
      this.bookingDetails = {
        start_date: this.facility.start_date,
        end_date: this.facility.end_date,
        children: storedChildren || this.globalGuests.children || 0,
        adults: storedAdults || this.globalGuests.adults || 0,
        catering_type: '',
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0,
        number_of_rooms: 0
      }
      this.calculateExtraPax()
      this.showDates = true
      this.dialog = true

      if (this.$store.dispatch) {
        if (this.$store._actions && this.$store._actions['get_facility']) {
          this.$store.dispatch('get_facility', accommodation.public_id)
        }
        if (this.$store._actions && this.$store._actions['get_facility_with_pricing']) {
          this.$store.dispatch('get_facility_with_pricing', {
            date: this.$moment(this.facility.start_date).format('YYYY-MM-DD'),
            public_id: accommodation.public_id
          })
            .then(() => this.$forceUpdate())
            .catch(() => {})
        }
      }
      this.$nextTick(() => {
        if (this.pricingWatcher) this.pricingWatcher()
        this.pricingWatcher = this.$watch('pricedFacility', (newVal) => {
          if (newVal && newVal.price && newVal.price.length > 0) this.$forceUpdate()
        }, { deep: true })
      })
    },

    closeBookingDialog() {
      this.dialog = false
      this.selectedFacility = null
      this.showDates = false
      this.showExtraPaxSection = false
      if (this.pricingWatcher) {
        this.pricingWatcher()
        this.pricingWatcher = null
      }
    },

    getDays() {
      if (!this.bookingDetails.start_date || !this.bookingDetails.end_date) return 1
      var start = this.$moment(this.bookingDetails.start_date)
      var end = this.$moment(this.bookingDetails.end_date)
      var days = end.diff(start, 'days')
      return days > 0 ? days : 1
    },

    getRoomRate() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var roomRate = Number(priceData.mandatory_price || priceData.price || priceData.adult_price || 0)
      var converted = this.convertCurrency(priceData.currency_type, roomRate)
      return converted ? converted.amount : 0
    },
    getAdultPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) {
        return this.getRoomRate()
      }
      var adultPrice = Number(priceData.adult_price || priceData.price || 0)
      var converted = this.convertCurrency(priceData.currency_type, adultPrice)
      return converted ? converted.amount : 0
    },
    getChildPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) {
        return this.getRoomRate()
      }
      var childPrice = Number(priceData.child_price || priceData.price || 0)
      var converted = this.convertCurrency(priceData.currency_type, childPrice)
      return converted ? converted.amount : 0
    },
    getExtraAdultRate() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var rate = Number(
        this.pricedFacility.cost_per_extra_adult ||
        priceData.cost_per_extra_adult ||
        priceData.adult_price ||
        priceData.mandatory_price ||
        priceData.price ||
        0
      )
      var converted = this.convertCurrency(priceData.currency_type, rate)
      return converted ? converted.amount : 0
    },
    getExtraChildRate() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var rate = Number(
        this.pricedFacility.cost_per_extra_child ||
        priceData.cost_per_extra_child ||
        priceData.child_price ||
        0
      )
      if (rate === 0 && priceData.adult_price) {
        rate = Number(priceData.adult_price) * 0.5
      }
      var converted = this.convertCurrency(priceData.currency_type, rate)
      return converted ? converted.amount : 0
    },
    calculateAdultTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) return 0
      var priceData = this.pricedFacility.price[0]
      var adultPrice = Number(priceData.adult_price || 0)
      var adults = Number(this.bookingDetails.adults || 0)
      var total = adultPrice * adults * this.getDays()
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },
    calculateChildTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      if (this.pricedFacility.accomodation_type_id === 'Stables' && this.pricedFacility.maximum_guests <= 2) return 0
      var priceData = this.pricedFacility.price[0]
      var childPrice = Number(priceData.child_price || 0)
      var children = Number(this.bookingDetails.children || 0)
      var total = childPrice * children * this.getDays()
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },
    calculateExtraAdultTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var extraAdults = Number(this.bookingDetails.extra_pax_adults || 0)
      var days = this.getDays()
      var rate = Number(
        this.pricedFacility.cost_per_extra_adult ||
        priceData.cost_per_extra_adult ||
        priceData.adult_price ||
        priceData.mandatory_price ||
        priceData.price ||
        0
      )
      var total = rate * extraAdults * days
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },
    calculateExtraChildTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var extraChildren = Number(this.bookingDetails.extra_pax_children || 0)
      var days = this.getDays()
      var rate = Number(
        this.pricedFacility.cost_per_extra_child ||
        priceData.cost_per_extra_child ||
        priceData.child_price ||
        0
      )
      if (rate === 0 && priceData.adult_price) {
        rate = Number(priceData.adult_price) * 0.5
      }
      var total = rate * extraChildren * days
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },
    getPelicanPackagePrice() {
      if (!this.pricedFacility || !this.pricedFacility.price) return 0
      if (!this.bookingDetails.catering_type) return 0
      var packagePrice = 0
      var currencyType = this.pricedFacility.price[0].currency_type
      if (this.bookingDetails.catering_type === '9a9a77eb') {
        for (var i = 0; i < this.pricedFacility.price.length; i++) {
          var item = this.pricedFacility.price[i]
          if (item.hasOwnProperty('lower_limit_list') && item.lower_limit_list.length > 0) {
            packagePrice = item.price
            currencyType = item.currency_type
            break
          }
        }
      } else if (this.bookingDetails.catering_type === '266677c3') {
        for (var j = 0; j < this.pricedFacility.price.length; j++) {
          var item2 = this.pricedFacility.price[j]
          if (item2.hasOwnProperty('upper_upper_limit_list') && item2.upper_upper_limit_list.length > 0) {
            packagePrice = item2.price
            currencyType = item2.currency_type
            break
          }
        }
      } else if (this.bookingDetails.catering_type === '377788d4') {
        for (var k = 0; k < this.pricedFacility.price.length; k++) {
          var item3 = this.pricedFacility.price[k]
          if (item3.hasOwnProperty('middle_middle_limit_list') && item3.middle_middle_limit_list.length > 0) {
            packagePrice = item3.price
            currencyType = item3.currency_type
            break
          }
        }
      }
      var converted = this.convertCurrency(currencyType, packagePrice)
      return converted ? converted.amount : 0
    },
    calculateTotalPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var days = this.getDays()
      var total = 0
      if (this.pricedFacility.accomodation_type_id === 'Stables') {
        if (this.pricedFacility.maximum_guests <= 2) {
          var roomRate = Number(priceData.mandatory_price || priceData.price || priceData.adult_price || 0)
          total = roomRate * days
        } else {
          var adultPrice = Number(priceData.adult_price || 0)
          var childPrice = Number(priceData.child_price || 0)
          var adults = Number(this.bookingDetails.adults || 0)
          var children = Number(this.bookingDetails.children || 0)
          total = (adultPrice * adults + childPrice * children) * days
        }
      } else if (this.pricedFacility.accomodation_type_id === 'Pelican' && this.bookingDetails.catering_type) {
        total = this.getPelicanPackagePrice() * days
      } else {
        var adultPrice2 = Number(priceData.adult_price || 0)
        var childPrice2 = Number(priceData.child_price || 0)
        var adults2 = Number(this.bookingDetails.adults || 0)
        var children2 = Number(this.bookingDetails.children || 0)
        total = (adultPrice2 * adults2 + childPrice2 * children2) * days
      }
      var convertedAmount = this.convertCurrency(priceData.currency_type, total)
      return convertedAmount ? convertedAmount.amount : 0
    },
    calculateTotalPriceWithExtras() {
      var baseTotal = this.calculateTotalPrice()
      var extraTotal = 0
      if (this.bookingDetails.extra_pax_adults > 0) {
        extraTotal += this.calculateExtraAdultTotal()
      }
      if (this.bookingDetails.extra_pax_children > 0) {
        extraTotal += this.calculateExtraChildTotal()
      }
      return this.$roundOff(baseTotal + extraTotal)
    },
    convertCurrency(currencyType, amount) {
      if (this.convert1) return this.convert1(currencyType, amount)
      return { amount: amount }
    },

    addAccommodation() {
      if (!this.selectedFacility) { alert('No facility selected'); return }
      if (!this.bookingDetails.start_date || !this.bookingDetails.end_date) {
        alert('Please select start and end dates'); return
      }
      if (this.bookingDetails.start_date === this.bookingDetails.end_date) {
        alert('Check-out date must be after check-in date'); return
      }
      if (this.selectedFacility.accomodation_type_id === 'Pelican' && !this.bookingDetails.catering_type) {
        alert('Please select a catering type for Pelican accommodation'); return
      }
      if (this.total === 0 &&
        Number(this.bookingDetails.extra_pax_adults || 0) === 0 &&
        Number(this.bookingDetails.extra_pax_children || 0) === 0) {
        alert('Please select at least one guest'); return
      }

      var facility = this.selectedFacility
      var priceData = facility.price && facility.price[0] ? facility.price[0] : null
      if (!priceData) { alert('No pricing available for the selected dates'); return }

      var adults = Number(this.bookingDetails.adults || 0)
      var children = Number(this.bookingDetails.children || 0)
      var extraAdults = Number(this.bookingDetails.extra_pax_adults || 0)
      var extraChildren = Number(this.bookingDetails.extra_pax_children || 0)

      var bookingData = {
        public_id: facility.public_id,
        catering_type: this.bookingDetails.catering_type,
        facility_type: facility.facility_type_id,
        accommodation_type: facility.accomodation_type_id,
        start_date: this.bookingDetails.start_date,
        end_date: this.bookingDetails.end_date,
        name: facility.name,
        children: children,
        adults: adults,
        extra_pax_adults: extraAdults,
        extra_pax_children: extraChildren,
        accommodation_discount: Number(this.bookingDetails.accommodation_discount || 0),
        number_of_rooms: Number(this.bookingDetails.number_of_rooms || 0)
      }

      if (facility.accomodation_type_id === 'Pelican') {
        let priceIndex
        let currencyList = []
        if (this.bookingDetails.catering_type === '9a9a77eb') {
          facility.price.map(function (item, index) {
            if (item.hasOwnProperty('lower_limit_list') && item.lower_limit_list.length > 0) {
              priceIndex = index
              currencyList = item.upper_currency
            }
          })
        } else if (this.bookingDetails.catering_type === '266677c3') {
          facility.price.map(function (item, index) {
            if (item.hasOwnProperty('upper_upper_limit_list') && item.upper_upper_limit_list.length > 0) {
              priceIndex = index
              currencyList = item.lower_currency
            }
          })
        } else if (this.bookingDetails.catering_type === '377788d4') {
          facility.price.map(function (item, index) {
            if (item.hasOwnProperty('middle_middle_limit_list') && item.middle_middle_limit_list.length > 0) {
              priceIndex = index
              currencyList = item.upper_currency
            }
          })
        }
        Object.assign(bookingData, {
          pack_prices: facility.price[priceIndex],
          pack_currency: facility.price[priceIndex],
          pelican_lower_currency: currencyList
        })
      } else if (facility.accomodation_type_id === 'Stables') {
        switch (Number(facility.maximum_guests)) {
          case 1: {
            var billableStandardAdults = adults - extraAdults
            if (billableStandardAdults < 0) billableStandardAdults = 0
            Object.assign(bookingData, {
              quantity: facility.quantity,
              maximum_guests: facility.maximum_guests,
              child_price: facility.price[0].child_price,
              mandatory_price: facility.price[0].mandatory_price,
              currency: facility.price[0].currency_type,
              adult_price: facility.price[0].mandatory_price * billableStandardAdults,
              cost_per_extra_adult: facility.price[0].mandatory_price,
              cost_per_extra_child: facility.price[0].child_price
            })
            break
          }
          case 2: {
            Object.assign(bookingData, {
              adult_price: facility.price[0].adult_price,
              child_price: facility.price[0].child_price,
              mandatory_price: facility.price[0].mandatory_price,
              currency: facility.price[0].currency_type,
              maximum_guests: facility.maximum_guests,
              cost_per_extra_adult: facility.price[0].mandatory_price,
              cost_per_extra_child: facility.price[0].child_price
            })
            break
          }
          default: {
            Object.assign(bookingData, {
              adult_price: facility.price[0].adult_price,
              child_price: facility.price[0].child_price,
              mandatory_price: facility.price[0].mandatory_price,
              currency: facility.price[0].currency_type,
              maximum_guests: facility.maximum_guests,
              cost_per_extra_adult: facility.price[0].mandatory_price,
              cost_per_extra_child: facility.price[0].child_price
            })
          }
        }
      } else {
        Object.assign(bookingData, {
          adult_price: facility.price[0].adult_price,
          child_price: facility.price[0].child_price,
          mandatory_price: facility.price[0].mandatory_price,
          currency: facility.price[0].currency_type,
          cost_per_extra_adult: facility.price[0].mandatory_price,
          cost_per_extra_child: facility.price[0].child_price
        })
      }

      var existingData = JSON.parse(localStorage.getItem('facility_data') || '[]')
      existingData.push(bookingData)
      localStorage.setItem('facility_data', JSON.stringify(existingData))

      this.$root.$emit('cart-updated')

      if (this.$store.commit) {
        this.$store.commit('ADD_FACILITY', bookingData)
      }

      alert(facility.name + ' has been added to your booking')
      this.closeBookingDialog()
    },

    developPricePacks(accommodation) {
      var prices = []
      if (accommodation.accomodation_type_id !== 'Stables' && accommodation.price) {
        for (var p = 0; p < accommodation.price.length; p++) {
          var price = accommodation.price[p]
          if (price.lower_limit_list) {
            for (var l = 0; l < price.lower_limit_list.length; l++) {
              prices.push({
                lower_limit: price.lower_limit_list[l],
                upper_limit: price.upper_limit_list[l],
                price: price.price,
                currency: price.currency_type
              })
            }
          }
        }
      }
      return prices
    }
  },

  watch: {
    storedBooking: {
      handler(newVal) {
        if (newVal.check_in && newVal.check_out && !this.facility.start_date) {
          this.facility.start_date = newVal.check_in
          this.facility.end_date = newVal.check_out
        }
      },
      deep: true
    },

    globalGuests: {
      handler: function (newGuests) {
        if (!newGuests) return
        if (this.bookingDetails.adults === 0 && this.bookingDetails.children === 0 &&
            (newGuests.adults > 0 || newGuests.children > 0)) {
          this.bookingDetails.adults = newGuests.adults
          this.bookingDetails.children = newGuests.children
          this.calculateExtraPax()
        }
      },
      deep: true
    },

    'bookingDetails.adults': {
      handler: function () {
        this.calculateExtraPax()
      }
    },

    'bookingDetails.children': {
      handler: function () {
        this.calculateExtraPax()
      }
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('PizzaDude.dk-Earthwerk.otf');
  font-weight: normal;
  font-style: normal;
}

.accommodation-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

.date-selection-card {
  border-radius: 12px;
  overflow: hidden;
}

.date-selection-header {
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
}

.date-selection-header h3 {
  margin: 0;
  color: #74a533;
}

.earth-color {
  color: #74a533;
  margin-left: 5%;
}

.slots-explanation {
  color: #666;
  font-size: 12px;
  margin-left: 5%;
  display: block;
}

.accommodations-grid-container {
  transition: opacity 0.2s ease;
}

.grid-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 600px) {
  .grid-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 960px) {
  .grid-wrapper {
    grid-template-columns: repeat(3, 1fr);
  }
}

.grid-item {
  display: flex;
  animation: cardFadeIn 0.25s ease both;
}

@keyframes cardFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.accommodation-card {
  background: rgba(255, 255, 255, 0.96);
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(165, 214, 167, 0.4);
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.accommodation-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 26px -10px rgba(0, 0, 0, 0.3);
}

.card-image-wrap {
  position: relative;
  width: 100%;
  cursor: pointer;
  overflow: hidden;
}

.card-image {
  border-radius: 0 !important;
  display: block;
  transition: transform 0.4s ease;
}

.accommodation-card:hover .card-image {
  transform: scale(1.04);
}

.price-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #8cc63f;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  letter-spacing: 0.2px;
}

.card-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.card-title {
  flex: 1;
  margin: 0;
  font-family: 'Earthwerk', sans-serif;
  font-size: 15px;
  line-height: 1.25;
  font-weight: 700;
  color: #2d4a1e;
  letter-spacing: 0.4px;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s ease;
}

.card-title:hover {
  color: #74a533;
}

.info-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eef3e8;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.info-btn:hover {
  background: #dbe8cc;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 11.5px;
  color: #7b8f6b;
}

.price-row {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eef1ea;
}

.price-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
  color: #5e6e4e;
  padding: 2px 0;
  gap: 8px;
}

.price-line > span:first-child {
  flex: 1;
}

.price-value {
  font-weight: 700;
  color: #e67e22;
  white-space: nowrap;
}

.add-btn {
  width: 100%;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #74a533;
  color: #fff;
  font-family: 'Earthwerk', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 12px 20px;
  border: none;
  border-radius: 40px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(116, 165, 51, 0.3);
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  margin-top: auto;
}

.add-btn:active {
  transform: scale(0.98);
}

.add-btn:disabled {
  background: #dce5d4;
  box-shadow: none;
  cursor: not-allowed;
}

.skeleton-card {
  pointer-events: none;
  animation: none;
}

.skeleton-image {
  width: 100%;
  aspect-ratio: 1.6 / 1;
  background: #eef1ea;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: #eef1ea;
  margin-top: 10px;
}
.skeleton-line.mt-16 { margin-top: 16px; }
.skeleton-line.w-70 { width: 70%; }
.skeleton-line.w-55 { width: 55%; }
.skeleton-line.w-40 { width: 40%; }

.skeleton-btn {
  height: 42px;
  border-radius: 40px;
  background: #eef1ea;
  margin-top: auto;
  margin-bottom: 0;
}

.shimmer {
  position: relative;
  overflow: hidden;
}
.shimmer::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.65),
    transparent
  );
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  100% { transform: translateX(100%); }
}

.empty-state {
  border-radius: 14px !important;
  background: #fafcf7 !important;
  border: 1px dashed #dbe8cc;
}

.info-dialog,
.booking-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.gallery-section {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
}

.gallery-container,
.single-image-container {
  width: 100%;
  height: 400px;
}

.image-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  position: relative;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.9);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-button.prev {
  left: 20px;
}

.nav-button.next {
  right: 20px;
}

.image-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 10;
}

.details-section {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.details-header {
  margin-bottom: 25px;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}

.facility-title {
  font-family: 'Earthwerk', sans-serif;
  color: #333;
  font-size: 32px;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.description-section {
  color: #555;
  line-height: 1.8;
  font-size: 16px;
  margin-bottom: 30px;
}

.dialog-header {
  background-color: #74a533;
  color: white;
  padding: 20px 24px;
}

.dialog-header h2 {
  font-family: 'Earthwerk', sans-serif;
  font-size: 24px;
  margin: 0;
}

.dialog-content {
  padding: 24px;
}

.tab-content {
  padding: 15px 0;
}

.date-pickers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.date-input {
  margin-bottom: 15px;
}

.participant-selectors {
  margin: 20px 0;
}

.selector-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.participant-input {
  margin-bottom: 15px;
}

.global-guests-info {
  margin: 15px 0;
}

.extra-pax-section {
  margin-top: 15px;
  padding: 10px;
  background-color: #fff3e0;
  border-radius: 8px;
  border-left: 4px solid #ff9800;
}

.additional-options {
  margin-top: 15px;
}

.option-input {
  margin-bottom: 15px;
}

.price-summary {
  margin-top: 25px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.price-breakdown {
  margin-top: 10px;
}

.price-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.price-line.extra-charge {
  color: #e67e22;
  font-weight: 500;
}

.price-total {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 2px solid #74a533;
  font-size: 16px;
  font-weight: bold;
}

.dialog-actions {
  padding: 16px 24px;
  border-top: 1px solid #eee;
}

.cancel-btn {
  margin-right: 10px;
}

.reserve-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
}

@media (max-width: 960px) {
  .earthwerk-font {
    font-size: 18px;
  }

  .date-pickers,
  .selector-group {
    grid-template-columns: 1fr;
  }

  .capacity-display {
    flex-direction: column;
    gap: 10px;
  }

  .gallery-container,
  .single-image-container {
    height: 300px;
  }

  .facility-title {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .grid-wrapper {
    gap: 12px;
  }

  .card-body {
    padding: 12px 14px 14px;
  }

  .card-title {
    font-size: 14px;
  }

  .price-badge {
    font-size: 11px;
    padding: 4px 10px;
    top: 10px;
    left: 10px;
  }

  .price-line {
    font-size: 12px;
  }

  .add-btn {
    font-size: 13px;
    padding: 10px 16px;
  }

  .gallery-container,
  .single-image-container {
    height: 250px;
  }

  .facility-title {
    font-size: 24px;
  }

  .nav-button {
    width: 35px;
    height: 35px;
  }

  .nav-button.prev {
    left: 10px;
  }

  .nav-button.next {
    right: 10px;
  }

  .image-counter {
    font-size: 12px;
    padding: 3px 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .shimmer::after { animation: none; }
  .grid-item { animation: none; }
  .accommodation-card,
  .accommodation-card:hover { transform: none; }
}
</style>